import {
  AdAccountResource,
  AdDataStreamResource,
  adDataStreamSourceConfig,
  adDataStreamSourceForPlatform,
  AdDataStreamSource,
  DefaultMetaConversionActionTypes,
  isAdDataStreamSource,
} from "@arrowsup/platform-dtos";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  Button,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  BooleanField,
  BooleanInput,
  BulkDeleteButton,
  Create,
  Datagrid,
  DateField,
  DateInput,
  Edit,
  FunctionField,
  List,
  ReferenceInput,
  Resource,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useGetOne,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { useAdPlatformsService } from "../../logic/ad-platforms/ad-platforms.service";
import { apiErrorMessage } from "../../logic/api/api-error-message";
import { omit } from "../../logic/omit";
import { CrudPermissions } from "../auth/crud-permissions";
import { TopHelpInfoText } from "../help/top-help-info-text";
import { adAccountLabel, defaultAdDataStartDate } from "./ad-platform-fields";

export const AdDataStreamResourceView = (perms: CrudPermissions) => (
  <Resource
    name="ad-data-stream"
    key="ad-data-stream"
    options={{ label: "Ad Data Streams" }}
    list={<AdDataStreamList perms={perms} />}
    create={perms.create ? AdDataStreamCreate : undefined}
    edit={perms.update ? AdDataStreamEdit : undefined}
    icon={SyncIcon}
  />
);

const sourceLabel = (name?: string) =>
  name && isAdDataStreamSource(name)
    ? adDataStreamSourceConfig[name].label
    : name ?? "—";

/** Queues a sync now; with a date, re-pulls from that day. */
const SyncButton: React.FC<{ resyncFrom?: string; label?: string }> = ({
  resyncFrom,
  label = "Sync now",
}) => {
  const record = useRecordContext<AdDataStreamResource>();
  const service = useAdPlatformsService();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);

  if (!record) {
    return null;
  }

  const sync = async () => {
    setBusy(true);
    try {
      await service.sync(record.id, resyncFrom);
      notify("Sync queued");
    } catch (e) {
      notify(apiErrorMessage(e), { type: "error" });
    } finally {
      setBusy(false);
      refresh();
    }
  };

  return (
    <Button
      size="small"
      disabled={busy || !record.enabled}
      onClick={(e) => {
        e.stopPropagation();
        void sync();
      }}
    >
      {label}
    </Button>
  );
};

const AdDataStreamList: React.FC<{ perms: CrudPermissions }> = ({ perms }) => (
  <>
    <TopHelpInfoText text="Each stream pulls one ad account's daily spend, clicks, impressions and conversions, and its campaigns, ad groups and ads, every 6 hours. Recent days are re-read since platforms restate them." />
    <List>
      <Datagrid
        rowClick={perms.update ? "edit" : false}
        bulkActionButtons={perms.delete ? <BulkDeleteButton /> : false}
      >
        <FunctionField
          label="Ad Account"
          render={(r: AdDataStreamResource) => adAccountLabel(r.adAccount)}
        />
        <FunctionField
          label="Source"
          render={(r: AdDataStreamResource) => sourceLabel(r.source.name)}
        />
        <BooleanField source="enabled" />
        <TextField source="startDate" label="Start Date" />
        <TextField source="syncedThroughDate" label="Synced Through" />
        <DateField source="lastSuccessAt" label="Last Synced" showTime />
        <TextField source="lastError" label="Last Error" />
        {perms.update && (
          <FunctionField label="" render={() => <SyncButton />} />
        )}
      </Datagrid>
    </List>
  </>
);

/** Meta's conversion action types and OpenAI's attribution. */
const SourceOptions: React.FC<{ source?: AdDataStreamSource }> = ({
  source,
}) => {
  switch (source) {
    case "meta-ads":
      return (
        <TextInput
          source="source.conversionActionTypes"
          label="Conversion action types"
          helperText={`Comma-separated Meta action types counted as conversions. Defaults to ${DefaultMetaConversionActionTypes.join(
            ", "
          )}. Don't list an aggregate like lead with its parts (offsite_conversion.fb_pixel_lead), or conversions count twice.`}
          fullWidth
        />
      );
    case "openai-ads":
      return (
        <BooleanInput
          source="source.includeViewThroughConversions"
          label="Include view-through conversions"
          helperText="Counts 1-day view-through conversions as well as 30-day click-through ones. Off matches the Conversions column in OpenAI's Ads Manager."
        />
      );
    default:
      return null;
  }
};

/** Sets the source from the chosen ad account's platform. */
const SourceFromAdAccount: React.FC = () => {
  const { setValue } = useFormContext();
  const adAccountId = useWatch<{ adAccount?: { id?: number } }>({
    name: "adAccount.id",
  }) as number | undefined;
  const { data: adAccount } = useGetOne<AdAccountResource>(
    "ad-account",
    { id: adAccountId as number },
    { enabled: adAccountId !== undefined }
  );
  const source = adAccount
    ? adDataStreamSourceForPlatform(adAccount.platform)
    : undefined;

  useEffect(() => {
    if (source) {
      setValue("source.name", source);
    }
  }, [source, setValue]);

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      <Typography>Source: {source ? sourceLabel(source) : "—"}</Typography>
      <SourceOptions source={source} />
    </Box>
  );
};

export const AdDataStreamCreate: React.FC = () => (
  <Create redirect="list">
    <SimpleForm
      defaultValues={{ enabled: true, startDate: defaultAdDataStartDate }}
    >
      <ReferenceInput source="adAccount.id" reference="ad-account">
        <SelectInput
          label="Ad Account"
          optionText={(a: AdAccountResource) => adAccountLabel(a)}
          fullWidth
          required
        />
      </ReferenceInput>
      <SourceFromAdAccount />
      <TextInput source="description" fullWidth />
      <BooleanInput source="enabled" />
      <DateInput
        source="startDate"
        label="Start Date"
        helperText="First day to pull. Can't change after the stream is created."
        required
      />
    </SimpleForm>
  </Create>
);

/** Re-pulls past days, e.g. after a platform restated them. */
const ResyncPanel: React.FC = () => {
  const [from, setFrom] = useState("");
  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "center", pb: 2 }}>
      <SyncButton />
      <MuiTextField
        type="date"
        size="small"
        label="Resync from"
        value={from}
        onChange={(e) => setFrom(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <SyncButton resyncFrom={from || undefined} label="Resync" />
    </Box>
  );
};

const EditSourceOptions: React.FC = () => {
  const record = useRecordContext<AdDataStreamResource>();
  const name = record?.source.name;
  return (
    <SourceOptions
      source={name && isAdDataStreamSource(name) ? name : undefined}
    />
  );
};

const readOnlyFields = [
  "adAccount",
  "startDate",
  "syncedThroughDate",
  "lastRunAt",
  "lastSuccessAt",
  "lastError",
  "createdAt",
  "updatedAt",
];

export const AdDataStreamEdit: React.FC = () => (
  <Edit
    redirect="list"
    transform={(data: Record<string, unknown>) => omit(data, readOnlyFields)}
  >
    <SimpleForm>
      <FunctionField
        label="Ad Account"
        render={(r: AdDataStreamResource) => adAccountLabel(r.adAccount)}
      />
      <TextInput source="description" fullWidth />
      <BooleanInput source="enabled" />
      <EditSourceOptions />
      <TextInput source="startDate" label="Start Date" disabled />
      <TextInput source="syncedThroughDate" label="Synced Through" disabled />
      <TextInput source="lastError" label="Last Error" disabled fullWidth />
      <ResyncPanel />
    </SimpleForm>
  </Edit>
);
